import React from 'react';
import { Trans } from '@lingui/macro';
import { Settings, Home, Users, List, HardDrive, MessageCircle, Building2, LayoutList } from 'lucide-react';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Workspace } from '../../../models/Workspace';
import { useMyPermissions } from '../../../context/WorkspacePermissionContext';
import { AdminBadge } from '../../components/AdminBadge';
import { GIGAPOD_ICON_SRC, GIGAPOD_LABEL } from '../../../const/Gigapod';

export type AdminView = 'overview' | 'settings' | 'members' | 'rooms' | 'storage' | 'inquiry' | 'company' | 'board' | 'gigapod';

interface WSAdminSidebarContentProps {
    workspace: Workspace;
    currentView: AdminView;
    onChangeView: (view: AdminView) => void;
    onClose?: () => void;
}

export const WSAdminSidebarContent: React.FC<WSAdminSidebarContentProps> = ({
    workspace,
    currentView,
    onChangeView,
    onClose
}) => {
    const handleViewChange = (view: AdminView) => {
        onChangeView(view);
        onClose?.();
    };

    const perms = useMyPermissions();

    return (
        <div className="flex flex-col bg-background rounded-panel h-full">
            <div className="p-4 border-b">
                <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-muted-foreground" />
                    <div className="flex flex-col min-w-0 gap-1">
                        <span className="font-semibold truncate text-sm">{workspace.name}</span>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <AdminBadge variant="sm" />
                            <Badge variant="outline" className="w-fit text-meta-sm h-5 px-1.5 font-normal text-muted-foreground"><Trans>Owner</Trans></Badge>
                        </div>
                    </div>
                </div>
            </div>

            <nav className="flex-1 p-3 space-y-6 overflow-y-auto">
                <div className="space-y-1">
                    <Button
                        variant={currentView === 'overview' ? 'secondary' : 'ghost'}
                        className="w-full justify-start font-medium text-foreground hover:bg-muted transition-colors"
                        onClick={() => handleViewChange('overview')}
                    >
                        <Home className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Overview</Trans>
                    </Button>
                </div>

                <div className="space-y-2">
                    <div className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"><Trans>Administration</Trans></div>
                    <div className="space-y-1">
                        {(perms?.canEditWorkspaceSettings !== false) && (
                            <Button
                                variant={currentView === 'settings' ? 'secondary' : 'ghost'}
                                className="w-full justify-start h-9 hover:bg-muted transition-colors"
                                onClick={() => handleViewChange('settings')}
                            >
                                <Settings className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Settings</Trans>
                            </Button>
                        )}
                        <Button
                            variant={currentView === 'members' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-muted transition-colors"
                            onClick={() => handleViewChange('members')}
                        >
                            <Users className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Members</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'rooms' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-muted transition-colors"
                            onClick={() => handleViewChange('rooms')}
                        >
                            <List className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Channels</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'storage' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-muted transition-colors"
                            onClick={() => handleViewChange('storage')}
                        >
                            <HardDrive className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Storage</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'inquiry' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-muted transition-colors"
                            onClick={() => handleViewChange('inquiry')}
                        >
                            <MessageCircle className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Inquiries</Trans>
                        </Button>
                        <Button
                            variant={currentView === 'company' ? 'secondary' : 'ghost'}
                            className="w-full justify-start h-9 hover:bg-muted transition-colors"
                            onClick={() => handleViewChange('company')}
                        >
                            <Building2 className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Company Info</Trans>
                        </Button>
                        {(perms?.canManageBoards !== false) && (
                            <Button
                                variant={currentView === 'board' ? 'secondary' : 'ghost'}
                                className="w-full justify-start h-9 hover:bg-muted transition-colors"
                                onClick={() => handleViewChange('board')}
                            >
                                <LayoutList className="w-4 h-4 mr-3 text-muted-foreground" /> <Trans>Board Policy</Trans>
                            </Button>
                        )}
                        {(perms?.canEditWorkspaceSettings !== false) && (
                            <Button
                                variant={currentView === 'gigapod' ? 'secondary' : 'ghost'}
                                className="w-full justify-start h-9 hover:bg-muted transition-colors"
                                onClick={() => handleViewChange('gigapod')}
                            >
                                <img src={GIGAPOD_ICON_SRC} alt="" aria-hidden="true" className="w-4 h-4 mr-3 object-contain" />
                                {GIGAPOD_LABEL}
                            </Button>
                        )}
                    </div>
                </div>
            </nav>
        </div>
    );
};
